import { c as _c } from "react/compiler-runtime";
import { InsightQA } from '@/components/Organisms/InsightQA/InsightQA';
import type { IToolbarAction } from '@/components/Organisms/PDFViewer/Toolbar/ToolbarActions';
import { ToolbarDropdownEmbedded, ToolbarDropdownPopover } from '@/components/Organisms/PDFViewer/Toolbar/ToolbarDropdown';
import type { IToolbarDropdownOption } from '@/components/Organisms/PDFViewer/Toolbar/ToolbarDropdownOptions';
import { ToolbarDropdownOptions } from '@/components/Organisms/PDFViewer/Toolbar/ToolbarDropdownOptions';
import { PDFPageTemplate } from '@/components/Templates/PdfPage/PDFPageTemplate';
import { useAppContext } from '@/contexts/AppContext/AppContext';
import useTranslation from '@/hooks/useTranslation';
import { ContentType, type IAttachment, type IFormChoice, type IInsightNoteDetail } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import AiGenerativeIcon from '@bloomberg/web-icons-react/AiGenerative';
import DownloadIcon from '@bloomberg/web-icons-react/Download';
import FileIcon from '@bloomberg/web-icons-react/File';
import FileExcelIcon from '@bloomberg/web-icons-react/FileExcel';
import FilePowerpointIcon from '@bloomberg/web-icons-react/FilePowerpoint';
import TranslateIcon from '@bloomberg/web-icons-react/Translate';
import type { Translate } from 'next-translate';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { AttachmentWithAllowance } from './useInsightExecSumActions';
import { Button } from '@/components/Atoms/Button/Button';
import styles from './InsightViewerPage.module.css';
import classNames from '@/utils/classNames';
interface IInsightViewerProps {
  pdfPath: string;
  watermark: string;
  insight: IInsightNoteDetail;
  attachmentsWithAllowance: AttachmentWithAllowance[];
  reportLocales: IFormChoice[];
  continueReading?: boolean;
  readingProgress?: {
    progressPercent: number;
    progressDetail?: {
      pageNumber: number;
    };
  };
  userAnnotations?: Record<string, unknown> | null;
}
const INSIGHT_QA_FEATURE_FLAG = 'FTR_INSIGHT_QA';
export const InsightViewerPage: FC<IInsightViewerProps> = t0 => {
  const $ = _c(83);
  const {
    watermark,
    insight,
    pdfPath,
    attachmentsWithAllowance,
    reportLocales,
    continueReading: t1,
    readingProgress,
    userAnnotations
  } = t0;
  const continueReading = t1 === undefined ? false : t1;
  const {
    t,
    lang
  } = useTranslation("insights");
  let t2;
  if ($[0] !== attachmentsWithAllowance) {
    t2 = attachmentsWithAllowance || [];
    $[0] = attachmentsWithAllowance;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = filterAttachments(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const filteredAttachment = t3;
  const {
    user,
    hasFeature
  } = useAppContext();
  const isGuestUser = user.isGuestUser;
  let t4;
  if ($[4] !== hasFeature || $[5] !== isGuestUser) {
    t4 = !isGuestUser && hasFeature(INSIGHT_QA_FEATURE_FLAG);
    $[4] = hasFeature;
    $[5] = isGuestUser;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const allowQA = t4;
  const [isQAOpen, setIsQAOpen] = useState(false);
  let actions;
  if ($[7] !== filteredAttachment || $[8] !== insight.url || $[9] !== isGuestUser || $[10] !== lang || $[11] !== reportLocales || $[12] !== t) {
    actions = [];
    if (!isGuestUser && filteredAttachment?.length > 0) {
      let t5;
      if ($[14] !== t) {
        t5 = t("common:Actions.download");
        $[14] = t;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      const label = t5;
      let t6;
      if ($[16] !== filteredAttachment || $[17] !== lang || $[18] !== t) {
        let t7;
        if ($[20] !== lang || $[21] !== t) {
          t7 = attachment => {
            const attachmentInfo = getAttachmentInfo(t, attachment);
            return {
              key: attachment.type,
              icon: attachmentInfo.icon,
              label: attachmentInfo.label,
              href: attachment.url + "?language=" + lang,
              dataAnalyticsTracking: buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.Attachment, `Insight: ${attachment.type}`)
            };
          };
          $[20] = lang;
          $[21] = t;
          $[22] = t7;
        } else {
          t7 = $[22];
        }
        t6 = filteredAttachment.map(t7);
        $[16] = filteredAttachment;
        $[17] = lang;
        $[18] = t;
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      const downloadItems = t6;
      let t7;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <DownloadIcon />;
        $[23] = t7;
      } else {
        t7 = $[23];
      }
      let t8;
      if ($[24] !== downloadItems) {
        t8 = <ToolbarDropdownOptions items={downloadItems} />;
        $[24] = downloadItems;
        $[25] = t8;
      } else {
        t8 = $[25];
      }
      let t9;
      if ($[26] !== label || $[27] !== t8) {
        t9 = <ToolbarDropdownPopover label={label} icon={t7}>{t8}</ToolbarDropdownPopover>;
        $[26] = label;
        $[27] = t8;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      let t10;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <DownloadIcon />;
        $[29] = t10;
      } else {
        t10 = $[29];
      }
      let t11;
      if ($[30] !== downloadItems) {
        t11 = <ToolbarDropdownOptions items={downloadItems} />;
        $[30] = downloadItems;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      let t12;
      if ($[32] !== label || $[33] !== t11) {
        t12 = <ToolbarDropdownEmbedded label={label} icon={t10}>{t11}</ToolbarDropdownEmbedded>;
        $[32] = label;
        $[33] = t11;
        $[34] = t12;
      } else {
        t12 = $[34];
      }
      let t13;
      if ($[35] !== t12 || $[36] !== t9) {
        t13 = {
          key: "download",
          content: t9,
          mobileContent: t12
        };
        $[35] = t12;
        $[36] = t9;
        $[37] = t13;
      } else {
        t13 = $[37];
      }
      actions.push(t13);
    }
    if (reportLocales?.length > 1) {
      let t5;
      if ($[38] !== t) {
        t5 = t("common:Labels.language");
        $[38] = t;
        $[39] = t5;
      } else {
        t5 = $[39];
      }
      const label_0 = t5;
      let t6;
      if ($[40] !== insight.url || $[41] !== reportLocales) {
        let t7;
        if ($[43] !== insight.url) {
          t7 = option => ({
            key: option.value,
            label: option.label,
            href: `${insight.url}/view?language=${option.value}`,
            dataAnalyticsTracking: buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.Attachment, "Insight: switchLocale")
          });
          $[43] = insight.url;
          $[44] = t7;
        } else {
          t7 = $[44];
        }
        t6 = reportLocales.map(t7);
        $[40] = insight.url;
        $[41] = reportLocales;
        $[42] = t6;
      } else {
        t6 = $[42];
      }
      const languageItems = t6;
      let t7;
      if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <TranslateIcon />;
        $[45] = t7;
      } else {
        t7 = $[45];
      }
      let t8;
      if ($[46] !== languageItems) {
        t8 = <ToolbarDropdownOptions items={languageItems} />;
        $[46] = languageItems;
        $[47] = t8;
      } else {
        t8 = $[47];
      }
      let t9;
      if ($[48] !== label_0 || $[49] !== t8) {
        t9 = <ToolbarDropdownPopover label={label_0} icon={t7}>{t8}</ToolbarDropdownPopover>;
        $[48] = label_0;
        $[49] = t8;
        $[50] = t9;
      } else {
        t9 = $[50];
      }
      let t10;
      if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <TranslateIcon />;
        $[51] = t10;
      } else {
        t10 = $[51];
      }
      let t11;
      if ($[52] !== languageItems) {
        t11 = <ToolbarDropdownOptions items={languageItems} />;
        $[52] = languageItems;
        $[53] = t11;
      } else {
        t11 = $[53];
      }
      let t12;
      if ($[54] !== label_0 || $[55] !== t11) {
        t12 = <ToolbarDropdownEmbedded label={label_0} icon={t10}>{t11}</ToolbarDropdownEmbedded>;
        $[54] = label_0;
        $[55] = t11;
        $[56] = t12;
      } else {
        t12 = $[56];
      }
      let t13;
      if ($[57] !== t12 || $[58] !== t9) {
        t13 = {
          key: "language",
          content: t9,
          mobileContent: t12
        };
        $[57] = t12;
        $[58] = t9;
        $[59] = t13;
      } else {
        t13 = $[59];
      }
      actions.push(t13);
    }
    $[7] = filteredAttachment;
    $[8] = insight.url;
    $[9] = isGuestUser;
    $[10] = lang;
    $[11] = reportLocales;
    $[12] = t;
    $[13] = actions;
  } else {
    actions = $[13];
  }
  const extraActions = actions;
  const lastReadPage = !isGuestUser ? readingProgress?.progressDetail?.pageNumber : null;
  const t5 = insight.title;
  let t6;
  if ($[60] !== t) {
    t6 = t("insights:InsightViewer.pageTitle");
    $[60] = t;
    $[61] = t6;
  } else {
    t6 = $[61];
  }
  const t7 = `${t5} | ${t6}`;
  let t8;
  if ($[62] !== isGuestUser || $[63] !== t) {
    t8 = !isGuestUser ? t("insights:executive_summary") : undefined;
    $[62] = isGuestUser;
    $[63] = t;
    $[64] = t8;
  } else {
    t8 = $[64];
  }
  const t9 = !isGuestUser ? insight.url : undefined;
  const t10 = !isGuestUser && continueReading ? lastReadPage : null;
  const t11 = !isGuestUser ? userAnnotations : null;
  let t12;
  if ($[65] !== allowQA || $[66] !== insight.id || $[67] !== isQAOpen) {
    t12 = allowQA ? handle => <><InsightQA insightId={String(insight.id)} isOpen={isQAOpen} onClose={() => setIsQAOpen(false)} pdfViewerHandle={handle} /><Button kind="primary" onClick={() => setIsQAOpen(true)} icon={<AiGenerativeIcon />} className={classNames(styles.button, {
        "cds-hidden": isQAOpen
      })} aria-label="Ask about this document"><span className={styles.buttonText}>Ask about this document</span></Button></> : undefined;
    $[65] = allowQA;
    $[66] = insight.id;
    $[67] = isQAOpen;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== extraActions || $[70] !== insight.id || $[71] !== lastReadPage || $[72] !== pdfPath || $[73] !== t10 || $[74] !== t11 || $[75] !== t12 || $[76] !== t8 || $[77] !== t9 || $[78] !== watermark) {
    t13 = {
      path: pdfPath,
      watermarkText: watermark,
      backLinkText: t8,
      backLinkUrl: t9,
      extraActions,
      contentId: insight.id,
      contentType: ContentType.insight,
      jumpToPage: t10,
      lastReadPage,
      initialAnnotations: t11,
      renderSidePanel: t12
    };
    $[69] = extraActions;
    $[70] = insight.id;
    $[71] = lastReadPage;
    $[72] = pdfPath;
    $[73] = t10;
    $[74] = t11;
    $[75] = t12;
    $[76] = t8;
    $[77] = t9;
    $[78] = watermark;
    $[79] = t13;
  } else {
    t13 = $[79];
  }
  let t14;
  if ($[80] !== t13 || $[81] !== t7) {
    t14 = <PDFPageTemplate pageTitle={t7} pdfViewerProps={t13} />;
    $[80] = t13;
    $[81] = t7;
    $[82] = t14;
  } else {
    t14 = $[82];
  }
  return t14;
};
const supportedDownloadOption = new Set(['abstract', 'powerpoint', 'excel', 'zip']);
export const filterAttachments = (attachments: AttachmentWithAllowance[]) => attachments.filter(attachment => attachment.withinLimit && supportedDownloadOption.has(attachment.type));
const getAttachmentInfo = (t: Translate, attachment: IAttachment): {
  icon: ReactNode;
  label: string;
} => {
  switch (attachment.type) {
    case 'abstract':
      return {
        icon: <FileIcon />,
        label: t('insights:InsightDownloadOption.abstract')
      };
    case 'powerpoint':
      return {
        icon: <FilePowerpointIcon />,
        label: t('common:ResourceType.charts')
      };
    case 'excel':
      return {
        icon: <FileExcelIcon />,
        label: t('common:ResourceType.data')
      };
    case 'zip':
      return {
        icon: <FileIcon />,
        label: t('insights:InsightDownloadOption.zip')
      };
    default:
      return {
        icon: <FileIcon />,
        label: attachment.type
      };
  }
};